import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { GiTempleGate } from "react-icons/gi";
import api, { imgUrl } from "../utils/api";
import Loader from "../components/Loader";
import EmptyState from "../components/EmptyState";

export default function ServiceList() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchServices = () => {
    setLoading(true);
    api
      .get("/services/admin/all")
      .then((res) => setServices(res.data))
      .catch(() => toast.error("Failed to load services"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete service "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success("Service deleted");
      setServices(services.filter((s) => s._id !== id));
    } catch (err) {
      toast.error("Failed to delete service");
    }
  };

  const togglePublished = async (service) => {
    try {
      const { data } = await api.put(`/services/${service._id}`, { published: !service.published });
      setServices(services.map((s) => (s._id === service._id ? data : s)));
      toast.success(data.published ? "Published" : "Unpublished");
    } catch (err) {
      toast.error("Failed to update service");
    }
  };

  const filtered = filter === "all" ? services : services.filter((s) => s.type === filter);

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl text-maroon-800">Services</h1>
        <Link
          to="/admin/services/new"
          className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
        >
          <FiPlus /> New Service
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5">
        {["all", "pooja", "astrology"].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === tab
                ? "bg-maroon-700 text-gold-100"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {tab === "all" ? "All" : tab === "pooja" ? "Pooja Services" : "Astrology Services"}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No services yet"
          subtitle="Click 'New Service' to add your first service."
        />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Image</th>
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Price</th>
                <th className="px-5 py-3 font-medium">Order</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s._id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-3">
                    <div className="w-12 h-12 rounded-lg bg-gold-50 overflow-hidden flex items-center justify-center">
                      {s.image ? (
                        <img src={imgUrl(s.image)} alt={s.title} className="w-full h-full object-cover" />
                      ) : (
                        <GiTempleGate className="text-saffron-400" size={22} />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-medium text-maroon-800 max-w-xs truncate">{s.title}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        s.type === "pooja"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {s.type === "pooja" ? "Pooja" : "Astrology"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {s.priceFrom === "Contact" ? (
                      <span className="text-gray-400">Contact</span>
                    ) : (
                      <span className="font-semibold">₹{s.priceFrom}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-600">{s.order}</td>
                  <td className="px-5 py-3">
                    <button
                      onClick={() => togglePublished(s)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        s.published
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      {s.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/services/edit/${s._id}`}
                        className="p-2 rounded-lg hover:bg-gray-100 text-saffron-600"
                      >
                        <FiEdit2 />
                      </Link>
                      <button
                        onClick={() => handleDelete(s._id, s.title)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-red-500"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
